// public/js/features/subscription/pricing-data.js
// Superb Asset Guardian price list — version 2.0 (from the Pricing sheet,
// tab "2.0 for editing", cols A–H). All prices are per DEVICE/SERVICE per MONTH,
// USD, min. 3-year contract term.
//
// Volume tiers are keyed by the MINIMUM total number of devices, tags AND
// sensors. The effective tier for the whole configuration is chosen by that
// TOTAL count (see tierIndexForCount). `prices` arrays align 1:1 with `tiers`.
// Underlying vendor hardware models are deliberately NOT disclosed here.
export const PRICING = {
  version: '2.0',
  currency: 'USD',
  intro:
    'Prices are per device / service and per month, in USD. They include the ' +
    'hardware (device) and access to the Superb Asset Guardian platform (service), ' +
    'and assume a minimum contract term of 3 years.',

  // Order matters — indexes map to the `prices` arrays below. Ranges reflect the
  // sheet's tier bands; `min` is the smallest count that lands in the tier.
  tiers: [
    { key: '1',   label: '1–5',     min: 1,   blurb: 'Pilot / trial' },
    { key: '6',   label: '6–50',    min: 6,   blurb: 'Small fleet' },
    { key: '51',  label: '51–250',  min: 51,  blurb: 'Growing operation' },
    { key: '251', label: '251–500', min: 251, blurb: 'Large fleet' },
    { key: '501', label: '501+',    min: 501, blurb: 'Enterprise scale' },
  ],

  categories: [
    {
      key: 'core', title: 'Core IoT tracking devices',
      products: [
        { key: 'tracepro',  name: 'Superb TracePro',  desc: 'Airworthy active tracker.',
          prices: [65, 45, 39, 33, 28], device: true, flagship: true,
          details: [
            'Active GPS + cellular tracking — reports its own position worldwide.',
            'Airworthy: certified for use on aircraft.',
            { html: 'Approved by a growing list of airlines — <a href="https://www.superbaero.com/airlines-tracker-approved" target="_blank" rel="noopener">see which airlines</a>.' },
            'Acts as a gateway for paired BLE tags & sensors on the same asset.',
            'Trip detection, chained journeys and geofence enter/exit alerts.',
            'Buffers positions while offline and replays them on reconnect.',
          ] },
        { key: 'locatepro', name: 'Superb LocatePro', desc: 'Non-airworthy active tracker — battery and wired options.',
          prices: [58.5, 40.5, 35.1, 29.7, 25.2], device: true,
          details: [
            'Active GPS + cellular tracking for non-airworthy assets (GSE, tools, containers).',
            'Battery-powered and wired variants available.',
            'Same live map, trips, geofencing and alerts as the TracePro.',
            'Acts as a gateway for paired BLE tags & sensors.',
          ] },
      ],
    },
    {
      key: 'sensors', title: 'Added-value tags & sensors',
      note: 'Used in combination with a core tracking device.',
      products: [
        { key: 'smart',    name: 'Superb Smart+',    desc: 'Tag for passive tracking of assets.',
          prices: [20, 13.8462, 12, 10.1538, 8.6154], device: true, needsTracker: true,
          details: [
            'Low-cost tag, located via nearby gateways / active trackers.',
            'Presence supervision — alerts if the tag leaves the asset.',
            'Optional tether: early “moving away” warning by signal strength.',
            'Multi-year battery; needs a tracker or gateway in range.',
          ] },
        { key: 'temp',     name: 'Superb Temp+',     desc: 'Temperature sensor.',
          prices: [20, 13.8462, 12, 10.1538, 8.6154], device: true, needsTracker: true,
          details: [
            'Live temperature readings with history charts.',
            'Threshold alerts (e.g. cargo-hold overheat) with anti-flap smoothing.',
            'Export readings to CSV / XLSX / PDF.',
          ] },
        { key: 'humidity', name: 'Superb Humidity+', desc: 'Humidity sensor.',
          prices: [20, 13.8462, 12, 10.1538, 8.6154], device: true, needsTracker: true,
          details: [
            'Live humidity readings with history charts.',
            'Threshold alerts with anti-flap smoothing.',
            'Export readings to CSV / XLSX / PDF.',
          ] },
        { key: 'engine',   name: 'Superb Engine+',   desc: 'Engine-pairing sensor.',
          prices: [24, 16.6154, 14.4, 12.1846, 10.3385], device: true, needsTracker: true,
          details: [
            'Pairs a beacon to an engine or engine stand.',
            'Supervised presence — warns if the engine and its stand separate.',
            'Can be anchored to a specific home gateway on another asset.',
          ] },
        { key: 'shock',    name: 'Superb Shock+',    desc: 'Shock sensor.',
          prices: [15, 10.3846, 9, 7.6154, 6.4615], device: true, needsTracker: true,
          details: [
            'Detects impacts and rough handling.',
            'Activated inside the TracePro / LocatePro — no separate device.',
            'Shock alerts delivered to browser & push.',
          ] },
      ],
    },
    {
      key: 'services', title: 'Added-value services',
      products: [
        { key: 'em',    name: 'Engine-stand management app', short: 'EM+',
          desc: 'Per active tracker — automatically ×  your TracePro count.',
          prices: [15, 10.3846, 9, 7.6154, 6.4615], device: false,
          checkbox: true, multiplyBy: 'tracepro',
          details: [
            'Access to the engine-stand management application.',
            'Billed per active tracker — enable it to include one licence per TracePro.',
          ] },
        { key: 'data',  name: 'Superb Data+',  short: 'Data+',
          desc: 'Per device — automatically ×  your total device count.',
          prices: [2, 2, 2, 2, 2], device: false,
          checkbox: true, multiplyBy: 'deviceCount',
          details: [
            'Extended retention of telemetry data and records.',
            'Billed per device — enable it to cover your whole fleet.',
          ] },
      ],
    },
  ],

  // Real platform capabilities included at EVERY tier (shown as "what's included").
  included: [
    'Live map & real-time tracking',
    'Geofencing & enter/exit alerts (browser + push)',
    'Trip history & chained journeys',
    'BLE sensor supervision (temperature, humidity, shock, engine)',
    'Reports & data export (CSV / XLSX / PDF)',
    'Metric / imperial units, multi-tenant, role-based access',
  ],

  // Shown as footnotes; not part of the recurring total.
  notes: [
    'Consulting, device installation & training fee — free of charge on long-term contracts; charged as a one-time fee on shorter-term contracts. Includes consulting on the optimal configuration, installation of devices and training at your location.',
    'Tags & sensors are used in combination with a core tracking device (active tracker or gateway) on the asset.',
  ],

  // Emphasised line at the very bottom of the page.
  disclaimer: 'All prices exclude VAT and shipping.',
};

// Flat product lookup + the volume-tier chooser.
export const ALL_PRODUCTS = PRICING.categories.flatMap(c => c.products.map(p => ({ ...p, category: c.key })));

// Highest tier whose `min` is still ≤ the total device count (≥0 → tier 0).
export function tierIndexForCount(deviceCount) {
  let idx = 0;
  PRICING.tiers.forEach((t, i) => { if (deviceCount >= t.min) idx = i; });
  return idx;
}

export const fmtUSD = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n);
